<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import type { Farm } from '@/types/Farm'
import type { FarmYield } from '@/types/farmYield'
import { getFarms } from '@/api/farmService'
import { fetchYields, createYield, updateYield, deleteYield } from '@/api/farmyield'
import { useNotificationStore } from '@/stores/notification'

const auth = useAuthStore()
const notificationStore = useNotificationStore()

const farms = ref<Farm[]>([])
const yields = ref<FarmYield[]>([])
const search = ref('')
const loading = ref(false)
const showModal = ref(false)
const editMode = ref(false)
const selectedId = ref<string | null>(null)

const form = ref<Partial<FarmYield>>({
  farm: '',
  crop_type: '',
  units: '',
  quantity_harvested: 0,
  harvest_date: new Date().toISOString().slice(0, 10),
  season_year: new Date().getFullYear(),
  notes: '',
})

const filtered = computed(() => {
  const s = search.value.toLowerCase().trim()
  const validYields = yields.value.filter(y => y != null)
  if (!s) return validYields
  return validYields.filter(
    (y) => y.farm_name?.toLowerCase().includes(s) || y.crop_type?.toLowerCase().includes(s),
  )
})


// const canEditOrDelete = (row: FarmYield) => row.created_by === auth.user?.id

const canEditOrDelete = (row: FarmYield): boolean => {
  if (!auth.user) return false
  return row.created_by === auth.user.id
}


async function loadData() {
  loading.value = true
  try {
    const [farmsRes, yieldsRes] = await Promise.all([getFarms(), fetchYields()])
    farms.value = farmsRes.data.results
    yields.value = Array.isArray(yieldsRes.data.results) ? yieldsRes.data.results : []
  } finally {
    loading.value = false
  }
}

function openCreate() {
    console.log('openCreate triggered')
  editMode.value = false
  selectedId.value = null
  form.value = {
    farm: '',
    crop_type: '',
    units: '',
    quantity_harvested: 0,
    harvest_date: new Date().toISOString().slice(0, 10),
    season_year: new Date().getFullYear(),
    notes: '',
  }
  showModal.value = true
}

function openEdit(row: FarmYield) {
  editMode.value = true
  selectedId.value = row.id
  form.value = {
    farm: row.farm,
    crop_type: row.crop_type,
    units: row.units,
    quantity_harvested: row.quantity_harvested,
    harvest_date: row.harvest_date,
    season_year: row.season_year,
    notes: row.notes,
  }
  showModal.value = true
}

async function save() {
  if (!form.value.farm || !form.value.crop_type || !form.value.units) {
    notificationStore.showWarning('Please fill in all required fields (Farm, Crop type, Units).')
    return
  }
  try {
    if (editMode.value && selectedId.value) {
      await updateYield(selectedId.value, form.value)
      notificationStore.showSuccess('Yield record updated successfully!')
    } else {
      await createYield(form.value)
      notificationStore.showSuccess('Yield record created successfully!')
    }
    showModal.value = false
    await loadData()
  } catch (err: any) {
    notificationStore.showError(err?.message || 'Failed to save yield record.')
  }
}


import ConfirmDeleteModal from '@/components/farm/ConfirmDeleteModal.vue'
import WelcomeBanner from '@/components/farm/supervisor/WelcomeBanner.vue'

const showDeleteModal = ref(false)
const selectedRow = ref<FarmYield |null >(null)

async function handleDelete() {
  if (!selectedRow.value) return
  try {
    await deleteYield(selectedRow.value.id)
    notificationStore.showSuccess('Yield record deleted successfully.')
    await loadData()
  } catch (err: any) {
    notificationStore.showError(err?.message || 'Failed to delete yield record.')
  } finally {
    showDeleteModal.value = false
  }
}

function openDeleteModal(row: FarmYield) {
  selectedRow.value = row
  showDeleteModal.value = true
}

const searchQuery = ref<string>('')

// Array of crop options
const cropOptions = ref<string[]>(['Wheat', 'Rice', 'Corn', 'Soybean', 'Barley', 'Potato'])

// Filtered options
const filteredOptions = ref<string[]>([])
const showDropdown = ref<boolean>(false)

// Methods
const filterOptions = (): void => {
  showDropdown.value = true
  const query = searchQuery.value.toLowerCase()
  filteredOptions.value = cropOptions.value.filter((option: string) =>
    option.toLowerCase().includes(query),
  )
}

const selectOption = (option: string): void => {
  form.value.crop_type = option
  searchQuery.value = option
  showDropdown.value = false
}

const addNewCrop = (): void => {
  if (searchQuery.value && !cropOptions.value.includes(searchQuery.value)) {
    cropOptions.value.push(searchQuery.value)
  }
  form.value.crop_type = searchQuery.value
  showDropdown.value = false
}

onMounted(loadData)
</script>

<template>
  <div class="p-4 md:p-6 max-w-7xl mx-auto">
    <WelcomeBanner />
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
      <h1 class="text-2xl font-bold">Yields</h1>
      <div class="flex items-center gap-3 w-full md:w-auto">
        <input
          v-model="search"
          type="text"
          placeholder="Search by farm or crop"
          class="w-full md:w-80 border rounded-lg px-3 py-2"
        />
        <button
          @click="openCreate"
          class="whitespace-nowrap bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
        >
          Add Yield
        </button>
      </div>
    </div>

    <ConfirmDeleteModal
      v-if="showDeleteModal"
      :item-name="selectedRow?.units + ' ' + selectedRow?.quantity_harvested + ' of ' + selectedRow?.crop_type + ' record for ' + selectedRow?.farm_name"
      @close="showDeleteModal = false"
      @confirm="handleDelete"
    />


    <div class="overflow-x-auto bg-white shadow-sm rounded-lg border">
      <table class="min-w-full text-sm">
        <thead class="bg-green-100 text-gray-700">
          <tr>
            <th class="p-3 text-left">Farm</th>
            <th class="p-3 text-left">Crop</th>
            <th class="p-3 text-left">Quantity</th>
            <th class="p-3 text-left">Units</th>
            <th class="p-3 text-left">Harvest Date</th>
            <th class="p-3 text-left">Season (Year)</th>
            <th class="p-3 text-left">Notes</th>
            <th class="p-3 text-left">Created By</th>
            <th class="p-3 text-left">Owner</th>
            <th class="p-3 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in filtered" :key="row.id" class="border-t hover:bg-green-50">
            <td class="p-3">{{ row.farm_name }}</td>
            <td class="p-3">{{ row.crop_type }}</td>
            <td class="p-3">{{ row.quantity_harvested }}</td>
            <td class="p-3">{{ row.units }}</td>
            <td class="p-3">{{ row.harvest_date }}</td>
            <td class="p-3">{{ row.season_year }}</td>
            <td class="p-3">{{ row.notes }}</td>
            <td class="p-3">{{ row.created_by_name }}</td>
            <td class="p-3">{{ row.owner_name }}</td>
            <td class="p-3">
              <div class="flex gap-2">
                <button
                  v-if="canEditOrDelete(row)"
                  @click="openEdit(row)"
                  class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                >
                  Edit
                </button>
                <!-- <button
                  v-if="canEditOrDelete(row)"
                  @click="removeRow(row.id)"
                  class="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                >
                  Delete
                </button> -->
                <button
                  v-if="canEditOrDelete(row)"
                  @click="openDeleteModal(row)"
                  class="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                >
                  Delete
                </button>

                <span v-else class="text-xs text-gray-500">View only</span>
              </div>
            </td>
          </tr>
          <tr v-if="!loading && filtered.length === 0">
            <td colspan="10" class="p-6 text-center text-gray-500">No yield records found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div class="bg-white w-full max-w-lg rounded-2xl shadow-lg p-6 relative">
        <button
          class="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          @click="showModal = false"
          aria-label="Close"
        >
          ✕
        </button>

        <h2 class="text-xl font-semibold mb-4">
          {{ editMode ? 'Edit Yield' : 'New Yield' }}
        </h2>

        <form @submit.prevent="save" class="space-y-4">
          <!-- Farm -->
          <div>
            <label class="block text-sm font-medium mb-1">Farm</label>
            <select v-model="form.farm" class="w-full border rounded-lg px-3 py-2" required>
              <option value="" disabled>Select a farm</option>
              <option v-for="f in farms" :key="f.id" :value="f.id">{{ f.name }}</option>
            </select>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- <div>
              <label class="block text-sm font-medium mb-1">Crop Type</label>
              <input
                v-model="form.crop_type"
                type="text"
                required
                class="w-full border rounded-lg px-3 py-2"
              />
            </div> -->
            <div>
              <label class="block text-sm font-medium mb-1">Crop Type</label>

              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search or add crop..."
                  class="w-full border rounded-lg px-3 py-2"
                  @input="filterOptions"
                  @keydown.enter.prevent="addNewCrop"
                />

                <!-- Dropdown options -->
                <ul
                  v-if="showDropdown && filteredOptions.length"
                  class="absolute z-10 w-full bg-white border rounded-lg mt-1 max-h-40 overflow-y-auto"
                >
                  <li
                    v-for="(option, index) in filteredOptions"
                    :key="index"
                    @click="selectOption(option)"
                    class="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    {{ option }}
                  </li>
                </ul>

                <!-- Add new crop option -->
                <div
                  v-if="showDropdown && !filteredOptions.length && searchQuery"
                  class="absolute z-10 w-full bg-white border rounded-lg mt-1 px-3 py-2 cursor-pointer hover:bg-gray-100"
                  @click="addNewCrop"
                >
                  ➕ Add "{{ searchQuery }}"
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">Units</label>
              <input
                v-model="form.units"
                type="text"
                placeholder="e.g., kg, bags"
                required
                class="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1">Quantity Harvested</label>
              <input
                v-model.number="form.quantity_harvested"
                type="number"
                step="0.01"
                min="0"
                required
                class="w-full border rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">Harvest Date</label>
              <input
                v-model="form.harvest_date"
                type="date"
                required
                class="w-full border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">Season (Year)</label>
            <input
              v-model.number="form.season_year"
              type="number"
              min="1900"
              max="2100"
              required
              class="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">Notes</label>
            <textarea
              v-model="form.notes"
              rows="3"
              class="w-full border rounded-lg px-3 py-2"
            ></textarea>
          </div>

          <div class="flex justify-end gap-3 pt-2">
            <button
              type="button"
              class="px-4 py-2 rounded-lg border hover:bg-gray-50"
              @click="showModal = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-5 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
            >
              {{ editMode ? 'Update' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* optional: no custom styles needed, Tailwind covers it */
</style>
